import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import { updatePasswordSchema } from "../../schemas";
import { toast } from "react-toastify";
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { useUpdatePasswordMutation } from "../../../../store/services/auth/auth.service";
import { useNavigate } from "react-router-dom";
import { IError } from "../../../../utils/getErrorMessage";
import { notifyError } from "../../../../utils/NotifyError";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import RecaptchaDisclaimer from "../../../../Components/RecaptchaDisclaimer";
import ButtonComponent from "../../../../Components/ButtonComponent";
const UpdatePassword = ({ token }: { token: string }) => {
	const [showPassword, setShowPassword] = useState(false);
	const [callUpdatePassword, { isLoading }] = useUpdatePasswordMutation();
	const [isUpdateLoading, setIsUpdateLoading] = useState(false);
	const navigate = useNavigate();
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	const handleOnSubmit = async (values: { password: string; confirmPassword: string }) => {
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsUpdateLoading(true);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
		} catch (e) {
			console.error(e);
		}

		callUpdatePassword({ password: values.password, token, recaptchaToken }).then(({ data, error }) => {
			if (data) {
				toast.success("Password updated successfully.", { autoClose: 3000 });
				navigate("/login", { replace: true });
			}

			if (error) {
				notifyError(error as IError);
			}
			setIsUpdateLoading(false);
		});
	};
	const {
		handleSubmit,
		handleChange,
		errors,
		values: { password, confirmPassword },
		dirty,
		isValid
	} = useFormik({
		validationSchema: updatePasswordSchema,
		initialValues: {
			password: "",
			confirmPassword: ""
		},
		onSubmit: handleOnSubmit
	});

	const handleClickShowPassword = () => setShowPassword(show => !show);

	return (
		<form onSubmit={handleSubmit} style={{ width: "100%" }}>
			<Box mb={2} className={"input_wrapper"}>
				<Typography variant={"subtitle1"} fontWeight={"bold"}>
					New Password
				</Typography>
				<TextField
					fullWidth
					type={showPassword ? "text" : "password"}
					value={password}
					name={"password"}
					onChange={handleChange}
					error={!!errors.password}
					helperText={errors.password}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label={showPassword ? "hide password" : "display password"}
										onClick={handleClickShowPassword}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							)
						}
					}}
				/>
			</Box>
			<Box mb={2} className={"input_wrapper"}>
				<Typography variant={"subtitle1"} fontWeight={"bold"}>
					Confirm Password
				</Typography>
				<TextField
					fullWidth
					type={showPassword ? "text" : "password"}
					value={confirmPassword}
					name={"confirmPassword"}
					onChange={handleChange}
					error={!!errors.confirmPassword}
					helperText={errors.confirmPassword}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label={showPassword ? "hide password" : "display password"}
										onClick={handleClickShowPassword}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							)
						}
					}}
				/>
			</Box>
			<Box width={"100%"}>
				<ButtonComponent
					fullWidth
					variant={"contained"}
					type="submit"
					disabled={(dirty && !isValid) || isLoading || isUpdateLoading}
					text={"Reset Password"}
					loading={isLoading || isUpdateLoading}
				/>
				<RecaptchaDisclaimer />
			</Box>
			<ReCAPTCHA
				sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
				size="invisible"
				ref={recaptchaRef}
				badge="bottomright"
			/>
		</form>
	);
};

export default UpdatePassword;
